<script lang="ts" setup>
    import { computed, ref, watch } from 'vue'
    import { useModuleStore } from '@/js/stores/modul'
    import { useModulHandbuchStore } from '@/js/stores/modulHandbuch'
    import { useFachbereichStore, useStudiengangStore } from '@/js/stores'
    import { useModuleAccess } from '@/js/components/Common/useModuleAccess'

    const props = defineProps({
        modulhandbuchId: {
            type: String,
            required: true
        },
        studiengangId: {
            type: String,
            required: true
        },
        fachbereichId: {
            type: String,
            required: true
        },
        semester: {
            type: String,
            required: true
        }
    })

    const emit = defineEmits<{
        modulCreated: [modulId: string]
    }>()

    const moduleStore = useModuleStore()
    const modulhandbuchStore = useModulHandbuchStore()
    const studiengangStore = useStudiengangStore()
    const fachbereichStore = useFachbereichStore()
    const { isSFBForStudiengang, isSDForFachbereich } = useModuleAccess()
    const isAdmin = computed(() => store.getters['security/isAdmin'])

    const isMHBCurrentOrFuture = computed(() => {
        const match = props.semester.match(/^(SoSe|WiSe)\s+(\d{4})/)
        if (!match) return true

        const mhbTyp = match[1]
        const mhbJahr = parseInt(match[2], 10)

        const now = new Date()
        const month = now.getMonth() + 1
        const year = now.getFullYear()

        let currentTyp, currentJahr
        if (month >= 4 && month <= 9) {
            currentTyp = 'SoSe'
            currentJahr = year
        } else if (month >= 10) {
            currentTyp = 'WiSe'
            currentJahr = year
        } else {
            currentTyp = 'WiSe'
            currentJahr = year - 1
        }

        if (mhbJahr !== currentJahr) return mhbJahr > currentJahr
        if (mhbTyp === currentTyp) return true
        return mhbTyp === 'WiSe' && currentTyp === 'SoSe'
    })

    const canCreate = computed(
        () => isAdmin.value || (isSDForFachbereich.value && isMHBCurrentOrFuture.value)
    )
    const canCustomizeModule = computed(() => isAdmin.value)

    const isOpen = ref(false)
    const modulname = ref('')
    const isCreating = ref(false)

    const selectedFachbereichId = ref(props.fachbereichId)
    const selectedStudiengangId = ref(props.studiengangId)
    const selectedModulhandbuchId = ref(props.modulhandbuchId)

    watch(isOpen, (val) => {
        if (val) {
            selectedFachbereichId.value = props.fachbereichId
            selectedStudiengangId.value = props.studiengangId
            selectedModulhandbuchId.value = props.modulhandbuchId
            modulname.value = ''
        }
    })

    const fachbereich = computed(() => fachbereichStore.getFachbereichById(selectedFachbereichId.value))
    const studiengang = computed(() => studiengangStore.getStudiengangById(selectedStudiengangId.value))
    const selectedSemester = computed(() =>
        canCustomizeModule.value
            ? (modulhandbuchStore.getModulHandbuchById(selectedModulhandbuchId.value)?.semester ?? '')
            : props.semester
    )

    const fachbereichOptions = computed(() =>
        fachbereichStore.getFachbereiche.map((fb) => ({
            title: fb.kurzName as string,
            value: fb.fachbereichId as string
        }))
    )
    const studiengangOptions = computed(() =>
        studiengangStore.studiengaenge
            .filter((sg) => sg.fachbereich === fachbereich.value?.['@id'])
            .map((sg) => ({ title: sg.kurzName as string, value: sg.studiengangId as string }))
    )
    const modulhandbuchOptions = computed(() =>
        modulhandbuchStore.modulHandbuecher
            .filter((mhb) => mhb.studiengangId === selectedStudiengangId.value)
            .map((mhb) => ({ title: mhb.semester as string, value: mhb.modulHandbuchId as string }))
    )

    function onFachbereichChange() {
        selectedStudiengangId.value = studiengangOptions.value[0]?.value ?? ''
        onStudiengangChange()
    }

    function onStudiengangChange() {
        selectedModulhandbuchId.value = modulhandbuchOptions.value[0]?.value ?? ''
    }

    async function submit(): Promise<void> {
        if (!modulname.value.trim()) return
        isCreating.value = true
        try {
            const newModul = await moduleStore.createModul(
                modulname.value.trim(),
                selectedStudiengangId.value,
                selectedSemester.value
            )
            await modulhandbuchStore.addModulToModulHandbuch(selectedModulhandbuchId.value, newModul.modulId)
            emit('modulCreated', newModul.modulId)
            isOpen.value = false
        } finally {
            isCreating.value = false
        }
    }
</script>

<template>
    <v-btn
        v-if="canCreate"
        block
        density="comfortable"
        class="font-weight-bold text-transform-none wrap-text"
        color="primary"
        prepend-icon="mdi-plus"
        @click="isOpen = true"
    >
        Modul hinzufügen
    </v-btn>

    <v-dialog v-model="isOpen" width="500">
        <v-card>
            <v-card-title>Modul hinzufügen</v-card-title>
            <v-card-text>
                <v-row class="mb-2" no-gutters>
                    <template v-if="canCustomizeModule">
                        <v-col cols="4" class="pr-2">
                            <v-select
                                v-model="selectedFachbereichId"
                                :items="fachbereichOptions"
                                label="Fachbereich"
                                density="compact"
                                hide-details
                                @update:model-value="onFachbereichChange"
                            />
                        </v-col>
                        <v-col cols="4" class="pr-2">
                            <v-select
                                v-model="selectedStudiengangId"
                                :items="studiengangOptions"
                                label="Studiengang"
                                density="compact"
                                hide-details
                                @update:model-value="onStudiengangChange"
                            />
                        </v-col>
                        <v-col cols="4">
                            <v-select
                                v-model="selectedModulhandbuchId"
                                :items="modulhandbuchOptions"
                                label="Modulhandbuch (Semester)"
                                density="compact"
                                hide-details
                            />
                        </v-col>
                    </template>
                    <template v-else>
                        <v-col cols="4">
                            <v-list-item
                                density="compact"
                                title="Fachbereich"
                                :subtitle="fachbereich?.kurzName ?? ''"
                            />
                        </v-col>
                        <v-col cols="4">
                            <v-list-item
                                density="compact"
                                title="Studiengang"
                                :subtitle="studiengang?.kurzName ?? ''"
                            />
                        </v-col>
                        <v-col cols="4">
                            <v-list-item density="compact" title="Semester" :subtitle="selectedSemester" />
                        </v-col>
                    </template>
                </v-row>
                <v-text-field v-model="modulname" label="Modulname" autofocus @keyup.enter="submit" />
            </v-card-text>
            <v-card-actions>
                <v-btn @click="isOpen = false">Abbrechen</v-btn>
                <v-btn color="success" :loading="isCreating" :disabled="!modulname.trim()" @click="submit">
                    Erstellen
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
    .wrap-text {
        height: auto !important;
        min-height: 36px;
    }

    .wrap-text :deep(.v-btn__content) {
        white-space: normal;
        padding-block: 6px;
    }
</style>
